// Pieces of the results page shared by the scoring v1 body (screens/Results.tsx) and the scoring v4
// body (components/ResultV4.tsx), so the two read the same.

import type { ReactNode } from "react";
import { Link } from "react-router";
import { HISTORY_MAX, changeLine } from "../lib/map";
import type { DomainCode, HistoryView, QuadrantName, SeriesView } from "../lib/types";
import { ResultMap, type MapRecord } from "./ResultMap";
import { Diamond } from "./Diamond";
import { Term } from "./Term";

/** "Oct 12, 2026" */
export const formatDate = (iso: string): string =>
  new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

/** The name and the three chips at the top: quadrant, home base, top gift; then a one-sentence summary (v4). */
export function ResultHero({
  name,
  quadrant,
  domainName,
  topGift,
  tips,
  marks,
  summary,
}: {
  name: string;
  quadrant: QuadrantName;
  domainName: string;
  topGift: string | undefined;
  /** What each chip means, shown on tap or hover. Without them the chips are plain text. */
  tips?: { quadrant?: ReactNode; domain?: ReactNode; gift?: ReactNode };
  /** The homepage's diamonds in the domain and gift chips (their colours). */
  marks?: { domain?: string; gift?: string };
  /** What the three chips mean, said in one go under them (v4, Edward Oct 22: "a synopsis at the top"). */
  summary?: ReactNode;
}) {
  const chip = (text: string, tip: ReactNode | undefined, mark?: string) => (
    <span className="inline-flex items-center gap-2">
      {mark ? <Diamond color={mark} /> : null}
      {tip ? <Term tip={tip}>{text}</Term> : text}
    </span>
  );
  return (
    <section className="mt-8" aria-labelledby="profile-name">
      <p className="eyebrow text-accent-text">You are</p>
      <h1 id="profile-name" className="display mt-3 text-[42px] leading-[1] break-words">
        {name}
      </h1>
      <ul className="mt-5 flex flex-wrap gap-2" aria-label="Your profile at a glance">
        <li className="rounded-full border border-accent-text px-3 py-1 text-[14px] font-semibold" style={{ backgroundColor: "color-mix(in srgb, var(--color-accent) 12%, transparent)" }}>
          {chip(quadrant, tips?.quadrant)}
        </li>
        <li className="rounded-full border border-line bg-panel px-3 py-1 text-[14px] font-semibold">{chip(domainName, tips?.domain, marks?.domain)}</li>
        {topGift ? (
          <li className="rounded-full border border-line bg-panel px-3 py-1 text-[14px] font-semibold">{chip(`${topGift} gift`, tips?.gift, marks?.gift)}</li>
        ) : null}
      </ul>
      {summary ? (
        <p className="mt-5 max-w-[60ch] text-[18px] leading-relaxed" data-testid="hero-summary">
          {summary}
        </p>
      ) : null}
    </section>
  );
}

export function ResultSection({ n, label, title, children }: { n: number; label?: string; title: string; children: ReactNode }) {
  const id = `section-${n}`;
  return (
    <section className="mt-14 border-t border-line pt-6" aria-labelledby={id}>
      <p className="flex items-baseline gap-3" aria-hidden={label ? undefined : true}>
        <span className="numeral text-[24px] text-accent-text" aria-hidden="true">
          {String(n).padStart(2, "0")}
        </span>
        {label ? <span className="eyebrow text-accent-text">{label}</span> : null}
      </p>
      <h2 id={id} tabIndex={-1} className="heading mt-2 text-[28px] leading-[1.1] outline-none">
        {title}
      </h2>
      <div className="mt-4">{children}</div>
    </section>
  );
}

export function ScoreCard({ label, value, description }: { label: string; value: number; description: string }) {
  return (
    <div className="rounded-[16px] border border-line bg-panel p-4">
      <p className="eyebrow text-muted">{label}</p>
      <p className="numeral mt-2 text-[42px]">{value}</p>
      <p className="mt-2 text-[14px] leading-snug text-muted">{description}</p>
    </div>
  );
}

/** Section 04: the write-up, in an ink card. */
export function MeaningCard({ text }: { text: string }) {
  return (
    <section className="mt-14 rounded-[16px] bg-card p-6 text-card-text sm:p-8" aria-labelledby="section-4">
      <p className="numeral text-[24px] text-card-accent" aria-hidden="true">
        04
      </p>
      <h2 id="section-4" className="eyebrow mt-2 text-card-accent">
        What this means
      </h2>
      <p className="mt-4 text-[19px] leading-snug">{text}</p>
    </section>
  );
}

/**
 * Section 01's body: the two scores, the map, and what an event's before/after or an earlier
 * take adds to it. Same for both versions; only the map record differs.
 */
export function FreedomClarityBody({
  record,
  domainNames,
  series,
  history,
  emailEnabled,
  takeId,
}: {
  record: MapRecord;
  domainNames: Record<DomainCode, string>;
  series?: SeriesView | null;
  history?: HistoryView | null;
  /** Results emails go out, so copy may point people to them. */
  emailEnabled: boolean;
  /** This take: with it, earlier profiles link to a comparison with it (not in the PDF). */
  takeId?: string;
}) {
  // Event series: with both takes in, the map shows the "after" dot solid and the "before" muted.
  const pair = series?.before && series.after ? { before: series.before, after: series.after } : null;
  const mapRecord = pair
    ? { ...record, freedom: pair.after.freedom, clarity: pair.after.clarity, quadrant: pair.after.quadrant }
    : record;
  // Retakes: earlier profiles this viewer has proved are theirs (not shown with the series overlay).
  const earlier = (history?.earlier ?? []).slice(0, HISTORY_MAX);
  const hiddenCount = history?.hiddenCount ?? 0;

  // "You've taken this 2 times: compare with Oct 28" (Edward, Nov 2), with the latest earlier one.
  const compareWith = takeId && !pair ? earlier[0] : undefined;
  const takenTimes = 1 + (history?.earlier.length ?? 0) + hiddenCount;

  return (
    <>
      {compareWith ? (
        <p className="mb-4 rounded-[12px] border border-line bg-soft px-4 py-3 text-[15px] print:hidden" data-testid="compare-earlier">
          You've taken this {takenTimes} times.{" "}
          <Link to={`/compare/${compareWith.takeId}/${takeId}`} className="whitespace-nowrap">
            Compare with {formatDate(compareWith.completedAt)}
          </Link>
        </p>
      ) : null}
      <div className="grid grid-cols-2 gap-3">
        <ScoreCard label="Freedom" value={record.freedom} description="How free you are from the beliefs that hold you back" />
        <ScoreCard label="Clarity" value={record.clarity} description="How clearly you know how you're built" />
      </div>
      {/* The caption and its map stay on one page in the PDF. */}
      <div className="break-inside-avoid">
        <p className="mt-5 text-[15px] text-muted">Clarity runs left to right. Freedom runs bottom to top.</p>
        <div className="mt-4 flex justify-center">
          <ResultMap record={mapRecord} domainNames={domainNames} before={pair ? pair.before : null} history={pair ? null : earlier} />
        </div>
      </div>
      {!pair && earlier.length > 0 ? (
        <ul className="mt-2 flex flex-wrap justify-center gap-x-4 gap-y-1 text-[12px] text-muted" aria-label="Earlier profiles on the map">
          {earlier.map((e, i) => (
            <li key={e.takeId} className="flex items-center gap-1.5">
              <span
                aria-hidden="true"
                className="inline-block h-2.5 w-2.5 rounded-full bg-muted"
                style={{ opacity: [0.75, 0.5, 0.3][i] ?? 0.3 }}
              />
              Earlier: {formatDate(e.completedAt)} (Freedom {e.freedom}, Clarity {e.clarity})
              {takeId ? (
                <Link to={`/compare/${e.takeId}/${takeId}`} className="print:hidden">
                  Compare
                </Link>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      {hiddenCount > 0 ? (
        <p className="mt-3 text-[14px] text-muted" data-testid="history-hidden">
          You have {hiddenCount} earlier {hiddenCount === 1 ? "profile" : "profiles"}.{" "}
          {/* A results email shows only its own profile (Nov 3): the rest need a sign-in link. */}
          {emailEnabled ? "Sign in with a link we email you to see them here." : "Open this page on the device you took them on to see them here."}
        </p>
      ) : null}
      {series ? <SeriesBlock series={series} emailEnabled={emailEnabled} /> : null}
    </>
  );
}

/** Event before/after summary in section 01. */
function SeriesBlock({ series, emailEnabled }: { series: SeriesView; emailEnabled: boolean }) {
  const { before, after } = series;
  if (before && after) {
    return (
      <div className="mt-4 rounded-[16px] border border-line bg-panel p-4" data-testid="series-block">
        <p className="eyebrow text-muted">{series.name} · Before → After</p>
        <p className="mt-2 text-[16px] font-semibold tabular-nums">{changeLine(before, after)}</p>
        <p className="mt-1 text-[16px]">
          {before.quadrant} → {after.quadrant}
        </p>
      </div>
    );
  }
  if (series.otherLocked) {
    const other = series.phase === "pre" ? "after" : "before";
    return (
      <p className="mt-4 rounded-[16px] border border-line bg-panel p-4 text-[16px] leading-snug" data-testid="series-block">
        Your {other} profile for {series.name} is linked to your email.{" "}
        {emailEnabled
          ? "Open the link in your results email to see both together."
          : `Open your results on the device you took the ${other} profile on to see both together.`}
      </p>
    );
  }
  if (before && !after && series.phase === "pre") {
    return (
      <p className="mt-4 rounded-[16px] border border-line bg-panel p-4 text-[16px] leading-snug" data-testid="series-block">
        You'll take it again after {series.name}. Use the same email and both results will appear here.
      </p>
    );
  }
  return null;
}
